import { createContext, useContext } from 'react';

export type MenuAlignment = 'left' | 'right' | 'center';

export interface MenuContextValue {
  /**
   * Name of the anchor element. This is used to position the menu content
   * relative to the anchor element.
   */
  anchorName: string;
  /**
   * DOM id of the popover element. Triggers point `popovertarget` at it, along
   * with `popovertargetaction="show"` so that tapping the trigger closes the
   * menu instead of re-opening it. Auto-generated from anchorName.
   */
  popoverId: string;
  isOpen: boolean;
  open(): void;
  close(): void;
  toggle(): void;
  closeOnSelect: boolean;
  align: MenuAlignment;
  lightDismiss?: boolean;
}

// Current menu — always defined inside a Menu, hook throws if missing
export const MenuContext = createContext<MenuContextValue | null>(null);

// Parent menu — null at the root level
export const ParentMenuContext = createContext<MenuContextValue | null>(null);

// Root menu — null outside any menu; a Menu self-assigns if none exists above it
export const RootMenuContext = createContext<MenuContextValue | null>(null);

// Context for elements to determine if they are a menu trigger or submenu trigger
export const MenuTriggerContext = createContext<boolean>(false);
export const SubmenuTriggerContext = createContext<boolean>(false);

export function useMenuContext(): MenuContextValue {
  const ctx = useContext(MenuContext);
  if (!ctx) {
    throw new Error('useMenuContext must be used within a Menu');
  }
  return ctx;
}

export function useRootMenuContext(): MenuContextValue {
  const ctx = useContext(RootMenuContext);
  if (!ctx) {
    throw new Error('useRootMenuContext must be used within a Menu');
  }
  return ctx;
}

export function useIsMenuTrigger() {
  return useContext(MenuTriggerContext);
}

export function useIsSubmenuTrigger() {
  return useContext(SubmenuTriggerContext);
}
